<template>
    <div class="editor__text text" @click="selector.closeSentenceControl">
        <Spinner v-if="loadingInitial" />
        <!-- TODO move tip to separate component -->
        <portal to="modals">
            <Modal
                v-if="!mouseTipsModalHide"
                name="mouse-hint"
                class="mouse-hint"
                @closed="mouseTipsModalHide = true"
            >
                <template #content>
                    <div>{{ $t('editor.text.tipLeftClick') }}</div>
                    <div>{{ $t('editor.text.tipRightClick') }}</div>

                    <button
                        class="btn"
                        @click="mouseTipsModalHide = true"
                        v-text="$t('confirmModal.ok')"
                    />
                </template>
            </Modal>
        </portal>
        <!-- TODO move to separate component -->
        <div
            v-if="showTopPanel"
            class="top-panel"
            :class="{ 'top-panel--reviewer-embed': isProofreadingReviewer }"
        >
            <!-- Professional Prooflistening (embedded outsourced reviewer): dedicated
                 top panel matching the mock (search / page pager / see-all-comments),
                 replacing the bare menubar this guest otherwise sees (no search
                 permission). Only the search input + match-count + prev/next-match
                 buttons stay gated by showSearch — the pager and comments pill must
                 always render for this reviewer regardless of search permission. -->
            <template v-if="isProofreadingReviewer">
                <div class="top-panel-pl__search-group">
                    <template v-if="showSearch">
                        <label class="top-panel-pl__search">
                            <IconSearchOutline class="top-panel-pl__search-icon" />
                            <TextSearch
                                v-if="book.id && book.limit"
                                ref="textSearchResPl"
                                :book="book.id"
                                hide-dropdown
                                class="top-panel-pl__search-field"
                                @select="onSentenceSearched"
                                @matchesChanged="onMatchesChanged"
                            />
                        </label>
                        <SearchMatchList
                            v-show="matchQuery.length >= 3"
                            :matches="searchMatches"
                            :index="matchIndex"
                            :query="matchQuery"
                            @select="goToMatchIndex"
                        />
                        <div v-if="matchCount" class="top-panel-pl__match-nav">
                            <button
                                type="button"
                                class="top-panel-pl__icon-button"
                                :title="$t('editor.text.prevMatch')"
                                :aria-label="$t('editor.text.prevMatch')"
                                @click="goToPrevMatch"
                            >
                                <IconChevronOutline direction="up" />
                            </button>
                            <button
                                type="button"
                                class="top-panel-pl__icon-button"
                                :title="$t('editor.text.nextMatch')"
                                :aria-label="$t('editor.text.nextMatch')"
                                @click="goToNextMatch"
                            >
                                <IconChevronOutline direction="down" />
                            </button>
                        </div>
                    </template>
                </div>
                <div class="top-panel-pl__right">
                    <span class="top-panel-pl__page-label">
                        {{ $t('editor.text.pageLabel') }}
                    </span>
                    <button
                        type="button"
                        class="top-panel-pl__page-nav-button"
                        :disabled="activePage <= 1"
                        :title="$t('editor.text.prevPage')"
                        :aria-label="$t('editor.text.prevPage')"
                        @click="goToPrevPage"
                    >
                        <IconChevronOutline direction="left" />
                    </button>
                    <div class="top-panel-pl__page-pill">
                        <PageInput
                            class="top-panel-pl__page-input"
                            :active-page="activePage"
                            :total-pages="totalPages"
                            :loading="loading"
                            :label="$t('editor.text.pageInput')"
                            @to-page="$emit('toPage', $event)"
                        />
                        <span class="top-panel-pl__page-pill-muted">
                            {{ $t('editor.text.pageOf', { total: totalPages }) }}
                        </span>
                    </div>
                    <button
                        type="button"
                        class="top-panel-pl__page-nav-button"
                        :disabled="activePage >= totalPages"
                        :title="$t('editor.text.nextPage')"
                        :aria-label="$t('editor.text.nextPage')"
                        @click="goToNextPage"
                    >
                        <IconChevronOutline direction="right" />
                    </button>
                    <div class="top-panel-pl__divider" />
                    <router-link class="top-panel-pl__comments" :to="commentsRoute">
                        <IconMessagesSquareOutline />
                        {{ $t('editor.text.seeAllComments') }}
                    </router-link>
                </div>
            </template>

            <!-- Default (non-reviewer-embed) look: unchanged from before this ticket. -->
            <template v-else>
                <template v-if="showSearch">
                    <TextSearch
                        v-if="book.id && book.limit"
                        ref="textSearchRes"
                        :book="book.id"
                        class="top-panel__search"
                        @select="onSentenceSearched"
                    />
                    <div class="top-panel__page">
                        Page
                        <input
                            v-autowidth="{ minWidth: '24px', maxWidth: '30px' }"
                            :value="activePage"
                            placeholder="#"
                            class="top-panel__page-input"
                            :disabled="loading"
                            type="text"
                            @keypress="isNumber($event)"
                            @keypress.enter="onGoToPage($event)"
                        />
                        / {{ totalPages }}
                    </div>
                </template>
                <EditorMenubar :items="menubarItems" />
            </template>
        </div>
        <div v-if="isProofreadingReviewer" class="editor-reviewer-card">
            <slot name="reviewer-navigation" />
            <div class="editor-reviewer-card__main">
                <div ref="text" class="text__inner">
                    <transition name="fade" appear>
                        <div
                            v-if="pages.length"
                            ref="container"
                            class="text__container"
                            :class="{ 'text__container--rtl': isRtl }"
                        >
                            <div
                                v-if="book.parent"
                                style="
                                    width: 100%;
                                    font-size: 16px;
                                    border-top: 2px solid #ff6b06;
                                    border-bottom: 2px solid #ff6b06;
                                    height: 28px;
                                    line-height: 25px;
                                    text-align: center;
                                    margin-bottom: 20px;
                                "
                            >
                                это копия книги!
                            </div>
                            <EditorTextPage
                                v-for="page in pages"
                                :key="page.id"
                                :active="activePage"
                                :page="page"
                                :playing-part="playingPart"
                                :reviewer-inline-personages="
                                    reviewerInlinePersonagesByPage[page.id] || []
                                "
                                @playPart="onPlayPart"
                            />
                            <div style="height: 100px" />
                        </div>
                    </transition>
                </div>
            </div>
        </div>
        <div v-else ref="text" class="text__inner">
            <div class="text__container__personages-icons">
                <EditorPersonageIcon
                    v-for="(item, topOffset) in personagesOnPage"
                    :key="
                        topOffset +
                        (isFixed(topOffset) ? 'fixed' : '') +
                        item.personages.map((p) => p.id).join('-')
                    "
                    :class="{ fixed: isFixed(topOffset) }"
                    :style="getStyle(topOffset)"
                    :personages="item.personages"
                />
            </div>

            <transition name="fade" appear>
                <div
                    v-if="pages.length"
                    ref="container"
                    class="text__container"
                    :class="{ 'text__container--rtl': isRtl }"
                >
                    <div
                        v-if="book.parent"
                        style="
                            width: 100%;
                            font-size: 16px;
                            border-top: 2px solid #ff6b06;
                            border-bottom: 2px solid #ff6b06;
                            height: 28px;
                            line-height: 25px;
                            text-align: center;
                            margin-bottom: 20px;
                        "
                    >
                        это копия книги!
                    </div>
                    <EditorTextPage
                        v-for="page in pages"
                        :key="page.id"
                        :active="activePage"
                        :page="page"
                        :playing-part="playingPart"
                        @playPart="onPlayPart"
                    />
                    <div style="height: 100px" />
                </div>
            </transition>
        </div>
        <div class="text__bottom">
            <slot name="bottom" />
        </div>
    </div>
</template>

<script>
import { fetchPersonages } from '../../api/personages';
import TextSearch from '../TextSearch/TextSearch';
import SearchMatchList from '../SearchMatchList/SearchMatchList.vue';
import EditorTextPage from '../EditorTextPage/EditorTextPage';
import EditorMenubar from '../EditorMenubar/EditorMenubar';
import SuggestionsHighlightSwitcher from '../SuggestionsHighlightSwitcher/SuggestionsHighlightSwitcher';
import EditorPersonageIcon from '../EditorPersonageIcon/EditorPersonageIcon.vue';
import PageInput from './PageInput.vue';

import { mapActions, mapState, mapGetters } from 'vuex';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import {
    IconNoteedit,
    IconWarning,
    IconSearchOutline,
    IconChevronOutline,
    IconMessagesSquareOutline,
} from '@speechki-frontend/shared/ui/icons';
import { isRtlLanguage } from '@speechki-frontend/core/editor/utils';

export const getReviewerInlinePersonageGroups = (pages, personages) => {
    const personageBySpeakerId = personages.reduce((personagesBySpeaker, personage) => {
        personagesBySpeaker[personage.speaker.id] = personage;
        return personagesBySpeaker;
    }, {});

    return pages.flatMap((page) => {
        const groups = [];
        let currentGroup = null;

        const closeGroup = () => {
            if (!currentGroup) return;
            groups.push(currentGroup);
            currentGroup = null;
        };

        page.bookpartsList.forEach((bookPartId) => {
            const bookPart = page.bookparts[bookPartId];
            const speakerId = bookPart.speaker && bookPart.speaker.id;
            const isParagraphEnd = bookPart.type_part && bookPart.type_part.slug === 'paragraph_end';

            if (!currentGroup) {
                currentGroup = {
                    pageId: page.id,
                    startBookPartId: bookPartId,
                    endBookPartId: bookPartId,
                    personages: [],
                    speakerId,
                };
            } else if (!isParagraphEnd && speakerId && speakerId !== currentGroup.speakerId) {
                closeGroup();
                currentGroup = {
                    pageId: page.id,
                    startBookPartId: bookPartId,
                    endBookPartId: bookPartId,
                    personages: [],
                    speakerId,
                };
            }

            currentGroup.endBookPartId = bookPartId;

            const personage = personageBySpeakerId[speakerId || currentGroup.speakerId];
            if (personage && !currentGroup.personages.some((item) => item.id === personage.id)) {
                currentGroup.personages.push(personage);
            }

            if (isParagraphEnd) {
                closeGroup();
            }
        });

        closeGroup();

        return groups.map(({ speakerId, ...group }) => group);
    });
};

export default {
    name: 'EditorText',
    components: {
        PageInput,
        TextSearch,
        SearchMatchList,
        EditorTextPage,
        EditorMenubar,
        Modal,
        EditorPersonageIcon,
        IconSearchOutline,
        IconChevronOutline,
        IconMessagesSquareOutline,
    },
    inject: ['isEmbed', 'selector'],
    props: {
        pages: {
            type: Array,
            default: () => [],
        },
        playingPart: {
            type: Object,
            default: () => undefined,
        },
    },
    data() {
        return {
            reloadUnsub: undefined,
            mouseTipsModalHide: true,

            personages: [],
            personagesOnPage: [],
            reviewerInlinePersonages: [],
            fixedPersonageTopOffset: null,

            // Professional Prooflistening top panel only
            matchCount: 0,
            matchIndex: -1,
            searchMatches: [],
            matchQuery: '',
        };
    },
    computed: {
        ...mapState({
            book: (state) => state.book.book,
        }),
        ...mapGetters(['isStaff', 'isAdmin', 'isPartner', 'isGuestAccess', 'isReviewer']),
        ...mapGetters('guestAccess', ['guestAccessActionPermission']),

        ...mapState('pagedBookparts', {
            loading: (state) => state.loading,
            loadingInitial: (state) => state.loadingInitial,
            activePage: (state) => state.activePage,
        }),

        isRtl() {
            return isRtlLanguage(this.book.book_language);
        },
        ...mapGetters('pagedBookparts', ['totalPages']),

        isProofreadingReviewer() {
            return this.isEmbed && this.isReviewer;
        },

        reviewerInlinePersonagesByPage() {
            return this.reviewerInlinePersonages.reduce((groupsByPage, group) => {
                if (!groupsByPage[group.pageId]) {
                    groupsByPage[group.pageId] = [];
                }

                groupsByPage[group.pageId].push(group);
                return groupsByPage;
            }, {});
        },

        commentsRoute() {
            return {
                name: 'embed-corrections',
                params: { id: this.$route.params.id },
            };
        },

        menubarItems() {
            const { id } = this.$route.params;

            return [
                {
                    name: 'corrections',
                    embed: 'embed-corrections',
                    label: 'corrections.title',
                    params: { id },
                    icon: IconNoteedit,
                    filter: () => {
                        // Professional Prooflistening (embedded outsourced reviewer):
                        // this entry is replaced by the top panel's "See all
                        // comments" pill (same target route), so hide it here to
                        // avoid a duplicate link. Every other menubar entry/case
                        // is unaffected.
                        if (this.isProofreadingReviewer) return false;

                        const isAllowed =
                            !this.isGuestAccess ||
                            this.guestAccessActionPermission('correction', 'list');

                        return isAllowed;
                    },
                },
                {
                    name: 'suggestions',
                    icon: IconWarning,
                    embed: 'embed-suggestions',
                    filter: () => {
                        if (!this.isGuestAccess) return true;
                        return this.guestAccessActionPermission('suggestion', 'list');
                    },
                    childs: [
                        {
                            name: 'editor',
                            label: 'editor.suggestion.childs.highlight',
                            component: SuggestionsHighlightSwitcher,
                            embed: 'embed-suggestions',
                            params: { id },
                            query: { highlight: 1 },
                        },
                        {
                            name: 'suggestions',
                            label: 'editor.suggestion.childs.goto',
                            embed: 'embed-suggestions',
                            params: { id },
                        },
                    ],
                },
                // {
                //     name: 'shuffle',
                //     embed: 'embed-shuffle',
                //     label: 'editor.shuffle.menuItem',
                //     params: { id },
                //     icon: IconShuffle,
                //     filter: () => {
                //         if (!this.isGuestAccess) return true;
                //         return this.guestAccessActionPermission('book', 'random_parts');
                //     }
                // },
                {
                    collapsed: true,
                    name: 'broken-parts',
                    embed: 'embed-broken-parts',
                    label: 'editor.brokenParts.menuItem',
                    params: { id },
                    filter: () => {
                        if (!this.isGuestAccess) return true;
                        return this.guestAccessActionPermission('broken_parts', 'list');
                    },
                },
                {
                    collapsed: true,
                    name: 'stylesheet',
                    embed: 'embed-stylesheet',
                    label: 'stylesheet.menuItem',
                    params: { id },
                    filter: () => {
                        if (!this.isGuestAccess) return true;
                        return this.guestAccessActionPermission('style_rule', 'list');
                    },
                    switcher: true,
                },
                {
                    collapsed: true,
                    name: 'bookpartschanges',
                    label: 'bookPartsChanges.menuItem',
                    params: { id },
                    filter: () => {
                        return this.isAdmin;
                    },
                },
            ];
        },
        showTopPanel() {
            if (this.isProofreadingReviewer) return true;

            if (!this.isGuestAccess) return true;

            if (this.showSearch) {
                return true;
            }

            return this.menubarItems.some((item) => item.filter());
        },
        showSearch() {
            if (!this.isGuestAccess) return true;

            return this.guestAccessActionPermission('book', 'search');
        },
    },

    watch: {
        '$route.query.part'(id) {
            if (!id) {
                return;
            }
            //
            this.$nextTick(() => {
                this.scrollToPart(id);
            });
        },

        mouseTipsModalHide(val) {
            if (val) localStorage.setItem('mouseTipsModalHide', Date.now());
        },

        'book.id'() {
            this.fetchPersonages();
        },

        personages: {
            handler() {
                this.updatePersonagesOnPage();
            },
            immediate: true,
        },

        pages: {
            handler() {
                this.updatePersonagesOnPage();
            },
            deep: true,
        },
    },

    mounted() {
        this.$refs.text.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);

        window.addEventListener(`personages-updated`, () => {
            this.fetchPersonages();
        });
    },

    async created() {
        this.reloadUnsub = this.$store.subscribeAction((action) => {
            if (action.type === 'pagedBookparts/fetchInitialBookparts') {
                this.selector.closeSentenceControl();
            }
        });

        await Promise.all([this.fetchTextTypes(), this.fetchBumps(), this.fetchSwapPartTypes()]);
    },

    beforeUnmount() {
        if (this.reloadUnsub) {
            this.reloadUnsub();
        }

        this.$refs.text.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        ...mapActions(['fetchTextTypes', 'fetchBumps', 'fetchSwapPartTypes']),

        scrollToPart(id) {
            let el = this.$el.querySelector(`[data-part="${id}"]`);
            if (el) {
                this.$scrollTo(el, 250, {
                    container: this.$refs.text,
                    offset: -60,
                });
            }
        },

        onSentenceSearched(part) {
            this.$emit('search', part);
        },

        onPlayPart(event) {
            this.$emit('playPart', event);
            this.selector.closeSentenceControl();
        },

        isNumber(event) {
            const { keyCode } = event;

            if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
                event.preventDefault();
                return false;
            }
            return true;
        },

        onGoToPage(event) {
            if (event.target.value) {
                this.$emit('toPage', Number(event.target.value));
            }
        },

        // Professional Prooflistening top panel only — reuse the same
        // 'toPage' event contract as onGoToPage above.
        goToPrevPage() {
            if (this.activePage <= 1) return;
            this.$emit('toPage', this.activePage - 1);
        },

        goToNextPage() {
            if (this.activePage >= this.totalPages) return;
            this.$emit('toPage', this.activePage + 1);
        },

        onMatchesChanged({ count, index, matches, query }) {
            this.matchCount = count;
            this.matchIndex = index;
            this.searchMatches = matches;
            this.matchQuery = query;
        },

        goToMatchIndex(index) {
            if (this.$refs.textSearchResPl) {
                this.$refs.textSearchResPl.goToMatchIndex(index);
            }
        },

        goToPrevMatch() {
            if (this.$refs.textSearchResPl) {
                this.$refs.textSearchResPl.goToMatch(-1);
            }
        },

        goToNextMatch() {
            if (this.$refs.textSearchResPl) {
                this.$refs.textSearchResPl.goToMatch(1);
            }
        },

        handleScroll() {
            // For reviewers updatePersonagesOnPage recomputes
            // getReviewerInlinePersonageGroups over all pages and assigns a
            // new array, which doesn't depend on scroll position/viewport
            // size — it would just force every EditorTextPage's
            // reviewerParagraphs to recompute and rerender on every scroll
            // event for no reason. The pages/personages watchers already
            // keep it up to date when the data it actually depends on
            // changes.
            if (this.isProofreadingReviewer) return;
            this.updatePersonagesOnPage();
        },

        handleResize() {
            if (this.isProofreadingReviewer) return;
            this.updatePersonagesOnPage();
        },

        async fetchPersonages() {
            try {
                const { results } = await fetchPersonages({
                    limits: this.book.limit,
                    book: this.book.id,
                });
                this.personages = results;
            } catch (e) {
                console.error(e);
            }
        },

        updatePersonagesOnPage() {
            if (this.isProofreadingReviewer) {
                this.reviewerInlinePersonages = getReviewerInlinePersonageGroups(
                    this.pages,
                    this.personages
                );
                this.personagesOnPage = [];
                return;
            }

            if (this.personages.length === 0) return;

            const speakerIdToPersonage = this.personages.reduce((acc, personage) => {
                acc[personage.speaker.id] = personage;
                return acc;
            }, {});

            const getPersonageIdOrNull = (speakerId) => {
                const personage = speakerIdToPersonage[speakerId];
                return personage ? personage.id : null;
            };

            this.fixedPersonageTopOffset = null;

            this.personagesOnPage = this.pages
                .map((page) => Object.values(page.bookparts))
                .flat()
                .filter((item) => ['paragraph_end', 'text'].includes(item.type_part.slug))
                .reduce(
                    (acc, item, index, array) => {
                        if (index === 0) {
                            acc.lastPersonage = {
                                personage: getPersonageIdOrNull((item.speaker || {}).id),
                                bookPartId: item.id,
                            };
                            return acc;
                        }

                        const currentPersonage = getPersonageIdOrNull((item.speaker || {}).id);

                        if (acc.lastPersonage.personage !== currentPersonage) {
                            acc.result.push(acc.lastPersonage);
                            acc.lastPersonage = {
                                personage: currentPersonage,
                                bookPartId: item.id,
                            };
                        } else {
                            acc.lastPersonage.bookPartId = item.id;
                        }

                        if (index === array.length - 1) {
                            acc.result.push(acc.lastPersonage);
                        }

                        return acc;
                    },
                    {
                        result: [],
                    }
                )
                .result.filter((item) => item.personage !== null)
                .map((item) => {
                    if (!this.$refs.text) return item;
                    const container = this.$refs.container;
                    const scrollContainer = this.$refs.text;
                    const scrollTop = scrollContainer.scrollTop;

                    const el = document.querySelector(`[data-part="${item.bookPartId}"]`);
                    if (!el) return item;

                    const containerTop = container.getBoundingClientRect().top + scrollTop;
                    const elBottom = el.getBoundingClientRect().bottom + scrollTop;

                    item.topOffset = elBottom - containerTop;

                    if (item.topOffset > scrollContainer.offsetHeight + scrollTop - 30) {
                        this.fixedPersonageTopOffset = Math.min(
                            this.fixedPersonageTopOffset || item.topOffset,
                            item.topOffset
                        );
                    }

                    return item;
                })
                .filter((item, idx, arr) => {
                    if (!this.$refs.text) return true;
                    const scrollContainer = this.$refs.text;
                    const scrollTop = scrollContainer.scrollTop;
                    const containerHeight = scrollContainer.offsetHeight;

                    const sorted = arr
                        .map((i) => i.topOffset)
                        .filter((t) => typeof t === 'number')
                        .sort((a, b) => a - b);

                    let lastVisibleIdx = -1;
                    for (let i = 0; i < sorted.length; i++) {
                        if (sorted[i] <= scrollTop + containerHeight) {
                            lastVisibleIdx = i;
                        }
                    }

                    const allowedTopOffsets = new Set(sorted.slice(0, lastVisibleIdx + 2));
                    return allowedTopOffsets.has(item.topOffset);
                })
                .filter((item) => item.topOffset !== undefined)
                .reduce((acc, item) => {
                    if (item.personage === null) return acc;

                    if (!acc[item.topOffset]) {
                        acc[item.topOffset] = {
                            personagesIds: new Set(),
                        };
                    }

                    acc[item.topOffset].personagesIds.add(item.personage);
                    acc[item.topOffset].personages = Array.from(
                        acc[item.topOffset].personagesIds
                    ).map((id) => this.personages.find((p) => p.id === id));

                    return acc;
                }, {});
        },

        getStyle(topOffset) {
            let style = {};

            if (Number(topOffset) !== this.fixedPersonageTopOffset) {
                style['top'] = `${topOffset}px`;
            }

            return style;
        },

        isFixed(topOffset) {
            return Number(topOffset) === this.fixedPersonageTopOffset;
        },
    },
};
</script>

<style lang="scss" src="./EditorText.scss"></style>
